import React, { useState, useEffect } from 'react';
import { leadsApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiOutlineX, HiExclamationCircle } from 'react-icons/hi';
import { usePermissions } from '../../hooks/usePermissions';
import { formatValidationErrors } from '../../utils/errorHandler';
import { formatDateForInputIST, formatDateTimeShortIST, todayYmdIST } from '../../utils/timezoneDisplay';

const LeadForm = ({ lead, sources = [], statuses = [], onClose, onSuccess }) => {
  const { canCreate, canUpdate } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateLeads, setDuplicateLeads] = useState([]);
  const [formData, setFormData] = useState({
    full_name: lead?.full_name || '',
    guardian_name: lead?.guardian_name || '',
    dob: formatDateForInputIST(lead?.dob),
    gender: lead?.gender || '',
    contact_primary: lead?.contact_primary || '',
    contact_whatsapp: lead?.contact_whatsapp || '',
    email: lead?.email || '',
    address: lead?.address || '',
    inquiry_type: lead?.inquiry_type || 'therapy',
    source_id: lead?.source_id ? String(lead.source_id) : '',
    reference_details: lead?.reference_details || '',
    status_id: lead?.status_id ? String(lead.status_id) : '',
    remarks: lead?.remarks || '',
    next_follow_up: formatDateForInputIST(lead?.next_follow_up),
    next_appointment_date: formatDateForInputIST(lead?.next_appointment_date),
    next_appointment_time: lead?.next_appointment_time || ''
  });

  const today = todayYmdIST();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const checkDuplicateAndSubmit = async () => {
    // Check permissions
    if (lead && !canUpdate('leads')) {
      toast.error('You do not have permission to update leads');
      return;
    }
    if (!lead && !canCreate('leads')) {
      toast.error('You do not have permission to create leads');
      return;
    }
    
    if (!formData.contact_primary) {
      toast.error('Primary contact is required');
      return;
    }

    try {
      // Check for duplicate contact
      const response = await leadsApi.checkDuplicateContact(
        formData.contact_primary,
        lead?.id
      );

      if (response.data.isDuplicate && response.data.data.length > 0) {
        // Show duplicate warning dialog
        setDuplicateLeads(response.data.data);
        setShowDuplicateDialog(true);
      } else {
        // No duplicate, proceed with submission
        await performSubmit();
      }
    } catch (error) {
      console.error('Error checking duplicate:', error);
      // If check fails, proceed with submission anyway
      await performSubmit();
    }
  };

  const performSubmit = async () => {
    setLoading(true);

    try {
      if (lead) {
        await leadsApi.update(lead.id, formData);
        toast.success('Lead updated successfully');
      } else {
        // Remove status_id when creating new lead - backend will set default "New" status
        const { status_id, ...createData } = formData;
        await leadsApi.create(createData);
        toast.success('Lead created successfully');
      }
      onSuccess();
      setShowDuplicateDialog(false);
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check permissions
    if (lead && !canUpdate('leads')) {
      toast.error('You do not have permission to update leads');
      return;
    }
    if (!lead && !canCreate('leads')) {
      toast.error('You do not have permission to create leads');
      return;
    }
    await checkDuplicateAndSubmit();
  };

  const handleContinueDuplicate = () => {
    setShowDuplicateDialog(false);
    performSubmit();
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateDialog(false);
    setDuplicateLeads([]);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        {/* Duplicate Warning Dialog */}
        {showDuplicateDialog && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-60" onClick={handleCancelDuplicate} />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0">
                    <HiExclamationCircle className="w-8 h-8 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Duplicate Contact Number</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      A lead with this contact number already exists.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-amber-800 mb-2">Existing Lead(s):</p>
                  <div className="space-y-2">
                    {duplicateLeads.map((duplicateLead) => (
                      <div key={duplicateLead.id} className="text-sm text-amber-700">
                        <p className="font-medium">{duplicateLead.full_name}</p>
                        <p className="text-xs">Contact: {duplicateLead.contact_primary}</p>
                        <p className="text-xs">
                          Created: {formatDateTimeShortIST(duplicateLead.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCancelDuplicate}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleContinueDuplicate}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Continue Anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
            <h2 className="text-xl font-semibold text-gray-900">
              {lead ? 'Edit Lead' : 'New Lead'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <HiOutlineX className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Personal Info */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="label">Full Name *</label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Guardian Name</label>
                  <input
                    type="text"
                    name="guardian_name"
                    value={formData.guardian_name}
                    onChange={handleChange}
                    className="input"
                    placeholder="Name of parent/guardian"
                  />
                </div>
                <div>
                  <label className="label">Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    className="input"
                    max={todayYmdIST()}
                  />
                </div>
                <div>
                  <label className="label">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="input"
                  >
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Primary Contact *</label>
                  <input
                    type="tel"
                    name="contact_primary"
                    value={formData.contact_primary}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">WhatsApp</label>
                  <input
                    type="tel"
                    name="contact_whatsapp"
                    value={formData.contact_whatsapp}
                    onChange={handleChange}
                    className="input"
                    placeholder="Same as primary if empty"
                  />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="input"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Inquiry Info */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Inquiry Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Inquiry For *</label>
                  <select
                    name="inquiry_type"
                    value={formData.inquiry_type}
                    onChange={handleChange}
                    className="input"
                    required
                  >
                    <option value="therapy">Therapy</option>
                    <option value="assessment">Assessment</option>
                    <option value="special_case">Special Case</option>
                  </select>
                </div>
                <div>
                  <label className="label">Source</label>
                  <select
                    name="source_id"
                    value={formData.source_id}
                    onChange={handleChange}
                    className="input"
                  >
                    <option value="">Select source</option>
                    {sources && Array.isArray(sources) && sources.length > 0 ? (
                      sources.map(source => (
                        <option key={source.id} value={source.id}>{source.name}</option>
                      ))
                    ) : (
                      <option value="" disabled>No sources available</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="label">Reference Details</label>
                  <input
                    type="text"
                    name="reference_details"
                    value={formData.reference_details}
                    onChange={handleChange}
                    className="input"
                    placeholder="Doctor name, referrer details..."
                  />
                </div>
                {lead && (
                  <div>
                    <label className="label">Status</label>
                    <select
                      name="status_id"
                      value={formData.status_id}
                      onChange={handleChange}
                      className="input"
                    >
                      {(statuses && Array.isArray(statuses) ? statuses : []).map(status => (
                        <option key={status.id} value={status.id}>{status.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="label">Next Follow-up</label>
                  <input
                    type="date"
                    name="next_follow_up"
                    value={formData.next_follow_up}
                    onChange={handleChange}
                    className="input"
                  />
                </div>
              </div>
            </div>

            {/* Next Appointment */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Next Appointment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Appointment Date</label>
                  <input
                    type="date"
                    name="next_appointment_date"
                    value={formData.next_appointment_date}
                    onChange={handleChange}
                    className="input"
                    min={today}
                  />
                  <p className="text-xs text-gray-500 mt-1">Cannot select past dates</p>
                </div>
                <div>
                  <label className="label">Appointment Time</label>
                  <input
                    type="time"
                    name="next_appointment_time"
                    value={formData.next_appointment_time}
                    onChange={handleChange}
                    className="input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Remarks</label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    className="input"
                    rows={3}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn btn-primary">
                {loading ? 'Saving...' : (lead ? 'Update Lead' : 'Create Lead')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeadForm;

